import React, { useEffect, useState } from 'react';
import { api, API } from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { Gift, Copy, Check, Share2, QrCode, Users, Sparkles } from 'lucide-react';

export default function ReferralBanner() {
  const [share, setShare] = useState(null);
  const [stats, setStats] = useState({ sent: 0, joined: 0, free_credits: 0, friends: [] });
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    api.get('/patients/me/share-code').then(r => setShare(r.data)).catch(() => {});
    api.get('/patients/me/referrals').then(r => setStats(r.data)).catch(() => {});
  }, []);

  if (!share) return null;
  const link = `${window.location.origin}/accept-invite/${share.invite_code}`;
  const qrUrl = `${API}/invites/${share.invite_code}/qr`;

  const copy = async () => {
    const ok = await copyToClipboard(link, { silent: true });
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };
  const nativeShare = async () => {
    // Iter 133 — Bug fix: on desktop `navigator.share` is often undefined
    // (Safari on iOS is the primary supported target). Previous code hid
    // the button entirely on desktop AND the mobile path silently swallowed
    // errors with `.catch(() => {})`. Now we (a) always render the button
    // and (b) gracefully fall back to copying the link + surfacing a toast
    // when the Web Share API isn't available.
    const payload = {
      title: 'Physiogenic',
      text: 'Join me at Physiogenic — your first 15-min check-in is on the house when you book.',
      url: link,
    };
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (e) {
        // AbortError (user tapped cancel) is normal — don't fall through.
        if (e && (e.name === 'AbortError' || e.code === 20)) return;
      }
    }
    // Fallback for desktop / unsupported browsers → copy the link + toast.
    const ok = await copyToClipboard(link, { silent: true });
    try {
      const { toast } = await import('sonner');
      if (ok) {
        toast.success('Invite link copied — paste it wherever you like.');
      } else {
        toast.error('Could not copy. Long-press the code above to select it manually.');
      }
    } catch (_) { /* toast import failed — ignore */ }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  return (
    <div
      data-testid="referral-banner"
      className="md:col-span-12 relative overflow-hidden rounded-2xl border border-soft surface p-5 md:p-6"
      style={{
        backgroundImage:
          'radial-gradient(120% 80% at 100% 0%, rgba(214,140,69,0.10) 0%, transparent 60%), radial-gradient(120% 80% at 0% 100%, rgba(45,74,67,0.08) 0%, transparent 55%)',
      }}
    >
      <div className="flex flex-wrap items-start gap-5">
        <div className="flex items-start gap-3 flex-1 min-w-[260px]">
          <div className="w-11 h-11 rounded-xl bg-[#FFB000] flex items-center justify-center shrink-0">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFB000] font-semibold mb-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Refer a friend
            </div>
            <div className="font-heading font-bold text-xl md:text-2xl tracking-tight">
              Give a free 15-min check-in. Earn one for yourself.
            </div>
            <p className="text-sm text-fg-muted mt-1 max-w-xl">
              Share your invite code. When a friend books their first visit, you both unlock a complimentary 15-minute
              physiotherapy review with our clinicians.
            </p>
          </div>
        </div>

        <div className="ml-auto grid grid-cols-3 gap-2 min-w-[260px]">
          <Stat label="Invites sent" value={stats.sent} testid="ref-stat-sent" />
          <Stat label="Friends joined" value={stats.joined} testid="ref-stat-joined" highlight />
          <Stat label="Free check-ins" value={stats.free_credits} testid="ref-stat-credits" highlight />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="surface border border-soft rounded-xl px-4 py-2 flex items-center gap-3" data-testid="ref-code-block">
          <div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-fg-muted">Your code</div>
            <div className="font-mono tracking-[0.22em] text-lg font-bold text-brand">{share.invite_code}</div>
          </div>
          <div className="h-8 w-px bg-soft" />
          <button
            onClick={copy}
            data-testid="ref-copy-link"
            className="text-xs inline-flex items-center gap-1.5 text-brand hover:opacity-80"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Link copied' : 'Copy link'}
          </button>
        </div>

        <button
          onClick={() => setShowQR(s => !s)}
          data-testid="ref-toggle-qr"
          className="btn-secondary text-xs inline-flex items-center gap-1.5"
        >
          <QrCode className="w-3.5 h-3.5" /> {showQR ? 'Hide QR' : 'Show QR'}
        </button>

        {/* Iter 133 — Always render the button; the handler falls back to
            "copy link + toast" on desktop where `navigator.share` is missing. */}
        <button
          onClick={nativeShare}
          data-testid="ref-native-share"
          className="btn-primary text-xs inline-flex items-center gap-1.5"
        >
          <Share2 className="w-3.5 h-3.5" /> Share now
        </button>

        {stats.friends?.length > 0 && (
          <div className="text-xs text-fg-muted inline-flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            <span>
              {stats.friends.slice(0, 2).map(f => f.name?.split(' ')[0]).join(', ')}
              {stats.friends.length > 2 ? ` +${stats.friends.length - 2}` : ''} joined via you
            </span>
          </div>
        )}
      </div>

      {showQR && (
        <div className="mt-5 surface border border-soft rounded-xl p-4 inline-flex items-center gap-4" data-testid="ref-qr-panel">
          <img
            src={qrUrl}
            alt="Invite QR code"
            className="w-32 h-32 rounded-md bg-white p-1 border border-soft"
            data-testid="ref-qr-image"
          />
          <div className="text-xs text-fg-muted max-w-[260px]">
            Friends scan this with any phone camera. They land on a sign-up page that auto-applies your code.
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, highlight }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border border-soft px-3 py-2 text-center ${highlight ? 'bg-[#F2EBDB]' : 'bg-white/40'}`}
    >
      <div className="font-heading font-bold text-2xl leading-none">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-fg-muted mt-1">{label}</div>
    </div>
  );
}
